import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { theme } from '_constants/theme';
import { usePrevious } from '_hooks';
import { selectData, selectLoading } from '_store/files/selector';
import { downloadFile, getFiles, refreshParams } from '_store/files/slice';
import { selectActiveMainProductId } from '_store/order/selector';
import { selectCustomerId } from '_store/user/selector';

import Paginate from 'components/atomic/Paginate';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import FilesList from './FilesList';

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spaces.small}px;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const StyledEmptyDataContainer = styled.div`
  padding-top: ${theme.spaces.medium}px;
`;

const StyledPaginateContainer = styled.div`
  padding-top: ${theme.spaces.large}px;
`;

const FilesView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const productId = useSelector(selectActiveMainProductId);
  const prevProductId = usePrevious(productId);
  const loading = useSelector(selectLoading);
  const data = useSelector(selectData);

  const handleDownload = useCallback(
    (fileId, fileName) => {
      dispatch(downloadFile({ customerId, productId, fileId, fileName }));
    },
    [customerId, productId]
  );

  const handlePageChange = useCallback(
    (page) => {
      dispatch(refreshParams({ page }));
      dispatch(getFiles({ customerId, productId }));
    },
    [customerId, productId]
  );

  useEffect(() => {
    if (customerId && productId && prevProductId !== productId) {
      dispatch(getFiles({ customerId, productId }));
    }
  }, [customerId, prevProductId, productId]);

  return (
    <StyledCard>
      <StyledHeader>
        <StyledTitleContainer>
          <Text size={theme.fontSizes.medium} weight={600}>
            Most recent Books
          </Text>
        </StyledTitleContainer>
      </StyledHeader>

      <StyledBody>
        <FilesList items={data?.elements} loading={loading} onDownload={handleDownload} />
        {!data?.totalPages && (
          <StyledEmptyDataContainer>
            <Text size={theme.fontSizes.small} weight={400} color={theme.colors.gray.primary} centered>
              No data available
            </Text>
          </StyledEmptyDataContainer>
        )}
        <StyledPaginateContainer>
          <Paginate
            pageRangeDisplayed={0}
            pageCount={data?.totalPages}
            currentPage={data?.page}
            onPageChange={handlePageChange}
          />
        </StyledPaginateContainer>
      </StyledBody>
    </StyledCard>
  );
};

export default FilesView;
